import { AGE_GROUP_LABELS, type StudentSummaryDto } from '@bjj/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client';
import { useApiMutation } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { InviteForm, PendingInvitations } from '../components/Invitations';
import { ManagedChildForm } from '../components/Parents';
import {
  Badge,
  Card,
  ConfirmButton,
  ErrorMessage,
  formatDate,
  PageHeader,
  ProgressBar,
} from '../components/ui';

export function MembershipActions({
  userId,
  status,
  invalidate,
}: {
  userId: string;
  status: string;
  invalidate: string[][];
}) {
  const change = useApiMutation<{ action: string; reason?: string }>(
    ({ action, reason }) => ({
      path: `/admin/users/${userId}/${action}`,
      method: 'POST',
      body: reason ? { reason } : {},
    }),
    invalidate,
  );
  const remove = useApiMutation<void>(
    () => ({ path: `/admin/users/${userId}`, method: 'DELETE' }),
    invalidate,
  );
  return (
    <div>
      <div className="btn-row">
        {status === 'active' ? (
          <>
            <ConfirmButton
              className="btn small"
              onConfirm={() => change.mutate({ action: 'suspend' })}
            >
              Suspend
            </ConfirmButton>
            <ConfirmButton onConfirm={() => change.mutate({ action: 'revoke' })}>
              Revoke
            </ConfirmButton>
          </>
        ) : (
          <button
            type="button"
            className="btn small"
            onClick={() => change.mutate({ action: 'reactivate' })}
          >
            Reactivate
          </button>
        )}
        <ConfirmButton confirmLabel="Delete permanently?" onConfirm={() => remove.mutate()}>
          Delete
        </ConfirmButton>
      </div>
      <ErrorMessage error={change.error ?? remove.error} />
    </div>
  );
}

export function StudentsPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'active' | 'suspended' | 'revoked' | 'all'>('active');
  const students = useQuery({
    queryKey: ['students', q, status],
    queryFn: () =>
      api.request<{ items: StudentSummaryDto[]; total: number }>(
        `/students?limit=500&status=${status}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
      ),
  });
  return (
    <>
      <PageHeader
        title="Students"
        subtitle={students.data ? `${students.data.total} student(s)` : undefined}
      />
      {isOwner ? (
        <>
          <Card title="Invite a student">
            <InviteForm role="student" />
          </Card>
          <Card title="Add a child without their own login">
            <ManagedChildForm />
          </Card>
          <PendingInvitations roles={['student', 'parent']} />
        </>
      ) : null}
      <Card
        title="Roster"
        actions={
          <div className="btn-row">
            <input
              type="search"
              aria-label="Search students"
              placeholder="Search name or email"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {isOwner ? (
              <select
                aria-label="Status filter"
                value={status}
                onChange={(e) => setStatus(e.target.value as typeof status)}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="revoked">Revoked</option>
                <option value="all">All</option>
              </select>
            ) : null}
          </div>
        }
      >
        <ErrorMessage error={students.error} />
        <DataTable
          rows={students.data?.items}
          rowKey={(s) => s.user.id}
          empty="No students match."
          columns={[
            {
              key: 'name',
              header: 'Name',
              render: (s) => <Link to={`/students/${s.user.id}`}>{s.user.name}</Link>,
            },
            {
              key: 'email',
              header: 'Email',
              render: (s) =>
                s.user.managed ? (
                  <span className="muted">No login (parent follows)</span>
                ) : (
                  s.user.email
                ),
            },
            {
              key: 'group',
              header: 'Group',
              render: (s) => (s.user.ageGroup ? AGE_GROUP_LABELS[s.user.ageGroup] : '—'),
            },
            { key: 'status', header: 'Status', render: (s) => <Badge value={s.user.status} /> },
            {
              key: 'progress',
              header: 'Completion',
              render: (s) => (
                <ProgressBar percent={s.progress.percent} label={`${s.user.name} completion`} />
              ),
            },
            {
              key: 'mastered',
              header: 'Mastered',
              render: (s) => `${s.progress.mastered} / ${s.progress.total}`,
            },
            { key: 'last', header: 'Last graded', render: (s) => formatDate(s.lastGradedAt) },
            ...(isOwner
              ? [
                  {
                    key: 'actions',
                    header: 'Membership',
                    render: (s: StudentSummaryDto) => (
                      <MembershipActions
                        userId={s.user.id}
                        status={s.user.status}
                        invalidate={[['students']]}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
    </>
  );
}
